import type { Definition } from 'typescript-json-schema';
import type { DecoratorStep, DecoratorContext } from '../interfaces';
import { metadataKeys } from '../decorators';
import { ConditionEvaluator } from '../condition-evaluator';
import { resolveEnumFromProperty, applyFilteredEnumToProperty } from './enum-utils';

/**
 * Enums step - filters enum values based on conditions.
 * If multiple @enums decorators exist on the same property, the first matching condition wins.
 */
export class Enums implements DecoratorStep {
    readonly name = 'enums';

    /**
     * Applies enum filtering to all properties in the definition.
     *
     * @param target - The class prototype containing decorator metadata
     * @param definition - The schema definition to modify
     * @param context - Runtime context for condition evaluation
     */
    init(target: object, definition: Definition, context: DecoratorContext): void {
        if (!definition?.properties || !target) {
            return;
        }

        const evaluator = new ConditionEvaluator(context);

        for (const propertyName in definition.properties) {
            const property = definition.properties[propertyName] as Definition;
            const enumsMetadataList = Reflect.getMetadata(metadataKeys.enums, target, propertyName);

            if (!enumsMetadataList?.length) {
                continue;
            }

            // Find first matching condition (first match wins)
            for (const enumsMetadata of enumsMetadataList) {
                const { passed } = evaluator.evaluate(enumsMetadata);

                if (passed) {
                    const currentEnumValues = resolveEnumFromProperty(property, context);
                    if (currentEnumValues) {
                        // Filter to only allowed values that exist in original enum
                        const filteredValues = enumsMetadata.allowedValues.filter((v) => currentEnumValues.includes(v));
                        applyFilteredEnumToProperty(property, filteredValues, context);
                    }
                    break; // First match wins, stop processing
                }
            }
        }
    }
}
